"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState, Suspense, lazy } from "react";
import { ArrowLeft, Brain, Plus, Minus, CheckCircle2, Play, Activity } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import BrandMark from "@/components/BrandMark";
import UserMenu from "@/components/auth/UserMenu";
import LocaleSwitcher from "@/components/site/LocaleSwitcher";
import { useAuth } from "@/lib/auth/auth-context";
import { SECONDARY_LOCALE } from "@/i18n/config";
import { useAppLocale, useMessages, useT } from "@/i18n/context";
import LandingSkeleton from "@/components/LandingSkeleton";
import { ChannelLogo } from "@/components/landing/ChannelLogo";

const LandingBelowFold = lazy(() => import("@/components/landing/LandingBelowFold"));

export default function Landing() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const minDelay = new Promise<void>((r) => setTimeout(r, 1100));
    const winLoad = new Promise<void>((r) => {
      if (document.readyState === "complete") return r();
      window.addEventListener("load", () => r(), { once: true });
    });
    Promise.all([minDelay, winLoad]).then(() => setReady(true));
  }, []);

  const showSkeleton = !ready;

  return (
    <>
      <AnimatePresence>
        {showSkeleton && (
          <motion.div
            key="landing-skeleton"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <LandingSkeleton />
          </motion.div>
        )}
      </AnimatePresence>
      <div
        aria-hidden={showSkeleton}
        className={`landing-root bg-grayScale-900 text-white overflow-x-clip selection:bg-primary-main selection:text-white transition-opacity duration-500 ${
          showSkeleton ? "opacity-0" : "opacity-100"
        }`}
      >
        <Header />
        <Hero />
        <Suspense fallback={null}>
          <LandingBelowFold />
        </Suspense>
      </div>
    </>
  );
}

/* ============ HEADER ============ */
function Header() {
  const { status, isAuthenticated } = useAuth();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("features");
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = [
    { id: "features", label: t("common.nav.features", "امکانات"), to: "/features" },
    { id: "solutions", label: t("common.nav.solutions", "راهکارها"), to: "/solutions" },
    { id: "trendi", label: t("common.nav.ai", "هوش مصنوعی"), to: "/trendi" },
    { id: "pricing", label: t("common.nav.pricing", "تعرفه‌ها"), to: "/pricing" },
    { id: "customers", label: t("common.nav.customers", "مشتریان"), to: "/customers" },
    { id: "contact", label: t("common.nav.contact", "تماس با ما"), to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`relative w-full transition-all duration-500 ${
          scrolled
            ? "bg-grayScale-900/85 backdrop-blur-2xl border-b border-white/[0.08] shadow-[0_8px_32px_-12px_rgba(0,0,0,0.4)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        {scrolled && (
          <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary-main/40 to-transparent" />
        )}

        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-6 h-16">
          <a href="#hero" className="relative flex items-center gap-3 group flex-shrink-0">
            <BrandMark size={38} />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-black text-[15px] tracking-tight">
                {t("common.brandName", "ترندی اسمارت")}
              </span>
              <span className="text-[9px] text-primary-main mt-1 tracking-[0.25em] font-bold">
                {t("common.brandTagline", "TRENDI · SMART")}
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => {
              const isActive = active === l.id;
              return (
                <Link
                  key={l.id}
                  href={l.to}
                  onMouseEnter={() => setActive(l.id)}
                  className={`relative px-4 py-2 text-[13.5px] font-bold transition-colors ${
                    isActive ? "text-white" : "text-grayScale-400 hover:text-white"
                  }`}
                >
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-3 right-3 h-[2px] bg-gradient-to-r from-primary-main to-secondary-main rounded-full"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            <ThemeToggle variant="floating" />
            <LocaleSwitcher className="border-white/10 bg-white/[0.06] text-white hover:border-primary-main/40 hover:bg-white/[0.1]" />
            <div className="hidden md:block w-px h-6 bg-white/10" />
            {status === "loading" ? (
              <div className="hidden md:block h-10 w-10 rounded-lg bg-white/10 animate-pulse" />
            ) : isAuthenticated ? (
              <UserMenu variant="dark" />
            ) : (
              <>
                <Link
                  href="/auth"
                  className="hidden md:inline-flex text-[13.5px] font-bold text-grayScale-300 hover:text-white px-3 py-2 transition-colors"
                >
                  {t("common.auth.login", "ورود")}
                </Link>
                <Link
                  href="/auth"
                  className="group relative inline-flex items-center gap-1.5 bg-primary-main hover:bg-primary-400 text-white px-4 py-2 rounded-lg text-[13px] font-black overflow-hidden shadow-lg shadow-primary-main/30 transition-colors"
                >
                  <span>{t("common.auth.startFree", "شروع رایگان")}</span>
                  <ArrowLeft
                    size={14}
                    className="group-hover:-translate-x-0.5 transition-transform"
                  />
                </Link>
              </>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center"
              aria-label={t("common.menu", "منو")}
            >
              <motion.div animate={{ rotate: mobileOpen ? 90 : 0 }}>
                {mobileOpen ? <Minus size={18} /> : <Plus size={18} />}
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-3 mt-2 rounded-2xl bg-grayScale-900/95 backdrop-blur-2xl border border-white/10 p-3 grid grid-cols-2 gap-2 shadow-2xl"
          >
            {links.map((l) => (
              <Link
                key={l.id}
                href={l.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.04] border border-white/5 text-sm font-bold text-grayScale-200 hover:bg-white/[0.08] hover:border-primary-main/30"
              >
                {l.label}
                <ArrowLeft size={14} className="text-primary-main" />
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

/* ============ HERO ============ */
function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const t = useT();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.07]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      <div className="absolute top-10 -right-40 w-[600px] h-[600px] rounded-full bg-primary-main/25 blur-[140px] animate-pulse-scale" />
      <div
        className="absolute bottom-0 -left-40 w-[600px] h-[600px] rounded-full bg-secondary-main/20 blur-[140px] animate-pulse-scale"
        style={{ animationDelay: "2s" }}
      />

      <motion.div style={{ y, opacity }} className="relative container mx-auto px-6 z-10">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center max-w-7xl mx-auto">
          <div className="text-center lg:text-right">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-7"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-main opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-main" />
              </span>
              <span className="text-xs font-medium text-grayScale-300">
                {t("landing.hero.badge", "نسل تازه‌ای از تعاملات هوشمند")}
              </span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.15] tracking-tight mb-7">
              <SplitWord text={t("landing.hero.titleLine1", "هر پیام، هر کانال،")} delay={0.25} />
              <br />
              <span className="inline-block relative pb-2">
                <SplitWord
                  text={t("landing.hero.titleLine2", "یک مغز هوشمند")}
                  delay={0.7}
                  className="hero-gradient-text"
                />
                <motion.svg
                  className="absolute -bottom-1 left-0 w-full"
                  viewBox="0 0 300 12"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 1.6, duration: 1.2 }}
                >
                  <motion.path
                    d="M 5 6 Q 75 2, 150 6 T 295 6"
                    stroke="url(#g)"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="g" x1="0" x2="1">
                      <stop offset="0" stopColor="rgb(0 150 136)" />
                      <stop offset="1" stopColor="rgb(255 152 0)" />
                    </linearGradient>
                  </defs>
                </motion.svg>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
              className="text-base md:text-lg text-grayScale-400 max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed"
            >
              {t(
                "landing.hero.description",
                "ترندی اسمارت تمام دایرکت‌ها، کامنت‌ها و پیام‌های شما را در همهٔ کانال‌ها در یک داشبورد جمع می‌کند و با AI فارسی، هوشمند پاسخ می‌دهد."
              )}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10"
            >
              <Link
                href="/auth"
                className="group relative w-full sm:w-auto px-8 py-4 rounded-full bg-white text-grayScale-900 font-bold flex items-center justify-center gap-2 overflow-hidden shadow-2xl shadow-primary-main/20"
              >
                <span className="relative z-10">
                  {t("common.auth.startFreeTrial", "شروع رایگان ۱۴ روزه")}
                </span>
                <ArrowLeft
                  size={18}
                  className="relative z-10 group-hover:-translate-x-1 transition-transform"
                />
              </Link>
              <a
                href="#demo"
                className="group flex items-center gap-3 text-grayScale-300 hover:text-white transition-colors"
              >
                <span className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <Play size={16} className="ml-0.5" fill="currentColor" />
                </span>
                <span className="text-sm font-medium">
                  {t("landing.hero.watchDemo", "تماشای دموی زنده")}
                </span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-3 text-xs text-grayScale-500"
            >
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-primary-main" />{" "}
                {t("landing.hero.statBeyond", "خدمات فراتر از تصور")}
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-primary-main" />{" "}
                {t("landing.hero.statSetup", "راه‌اندازی ۵ دقیقه‌ای")}
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-primary-main" />{" "}
                {t("landing.hero.statBusinesses", "۵٬۰۰۰+ کسب‌وکار فعال")}
              </span>
            </motion.div>
          </div>

          <HeroShowcase />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-grayScale-500"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">
          {t("landing.hero.scroll", "scroll")}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-[1px] h-10 bg-gradient-to-b from-grayScale-500 to-transparent"
        />
      </motion.div>
    </section>
  );
}

function SplitWord({
  text,
  delay = 0,
  className = "",
}: {
  text: string;
  delay?: number;
  className?: string;
}) {
  const words = text.split(" ");
  return (
    <span className={`inline-block ${className}`}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 50, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ delay: delay + i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {w}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </span>
  );
}

/* ============ HERO SHOWCASE — orbital channels around AI core ============ */
function HeroShowcase() {
  const t = useT();
  const locale = useAppLocale();
  const messages = useMessages();
  const inboxRows =
    locale === SECONDARY_LOCALE && messages ? messages.landing.inbox : FA_INBOX_ROWS;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-[520px] sm:h-[580px] mx-auto w-full max-w-[520px]"
    >
      <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-white/[0.06] to-transparent border border-white/10 backdrop-blur-sm" />
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-primary-main/30 blur-3xl" />
      <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-secondary-main/30 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, duration: 0.7 }}
        className="absolute top-8 right-8 left-8 bottom-24 rounded-[2rem] bg-grayScale-900/90 border border-white/10 shadow-2xl shadow-primary-main/10 overflow-hidden backdrop-blur-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-alert-error-main/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-secondary-main/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-alert-success-main/60" />
          </div>
          <div className="text-[10px] tracking-[0.25em] text-grayScale-500 uppercase">
            {t("landing.showcase.inboxTitle", "Trendi inbox")}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-main animate-pulse" />
            <span className="text-[10px] text-grayScale-400">
              {t("landing.showcase.live", "live")}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {inboxRows.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.7 + i * 0.12 }}
              className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                i === 0
                  ? "bg-primary-main/10 border-primary-main/30"
                  : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04]"
              }`}
            >
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-grayScale-700 border border-white/10 flex items-center justify-center font-bold text-xs">
                  {r.avatar}
                </div>
                <div className="absolute -bottom-1 -left-1 w-5 h-5 rounded-full bg-grayScale-900 border border-white/10 flex items-center justify-center">
                  <ChannelLogo name={r.channel} className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs font-bold text-white truncate">{r.name}</span>
                  <span className="text-[10px] text-grayScale-500">{r.time}</span>
                </div>
                <p className="text-[11px] text-grayScale-400 truncate">{r.msg}</p>
              </div>
              {r.unread && (
                <div className="w-5 h-5 rounded-full bg-primary-main text-white text-[10px] font-bold flex items-center justify-center">
                  {r.unread}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.4 }}
          className="absolute bottom-4 left-4 right-4 px-4 py-3 rounded-2xl bg-gradient-to-r from-primary-main to-primary-400 flex items-center gap-2.5"
        >
          <div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center">
            <Brain size={14} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] text-white/80 mb-0.5">
              {t("landing.showcase.aiAnswering", "Trendi AI در حال پاسخ...")}
            </div>
            <div className="flex items-center gap-1">
              {[0, 0.2, 0.4].map((d) => (
                <motion.span
                  key={d}
                  className="w-1.5 h-1.5 rounded-full bg-white"
                  animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1, repeat: Infinity, delay: d }}
                />
              ))}
            </div>
          </div>
          <span className="text-[10px] font-bold text-white/90">
            {t("landing.showcase.responseTime", "۰.۳s")}
          </span>
        </motion.div>
      </motion.div>

      {[
        { name: "Instagram", pos: "top-2 -right-3", delay: 1.8, anim: { y: [-6, 6, -6] } },
        { name: "Telegram", pos: "top-1/3 -left-5", delay: 2.0, anim: { y: [6, -6, 6] } },
        { name: "WhatsApp", pos: "bottom-28 -right-5", delay: 2.2, anim: { y: [-4, 8, -4] } },
        { name: "Bale", pos: "-bottom-2 right-1/3", delay: 2.4, anim: { y: [4, -4, 4] } },
        { name: "Rubika", pos: "-bottom-2 left-1/4", delay: 2.6, anim: { y: [-4, 4, -4] } },
      ].map((c) => (
        <motion.div
          key={c.name}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: c.delay, type: "spring", stiffness: 140 }}
          className={`absolute ${c.pos} z-20`}
        >
          <motion.div
            animate={c.anim}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="w-12 h-12 rounded-2xl bg-grayScale-800 border border-white/15 flex items-center justify-center shadow-2xl backdrop-blur-xl"
          >
            <ChannelLogo name={c.name} className="w-7 h-7" />
          </motion.div>
        </motion.div>
      ))}

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.6 }}
        className="absolute top-2 left-4 bg-grayScale-800/90 backdrop-blur-xl border border-white/10 rounded-full px-3 py-1.5 shadow-xl flex items-center gap-2 z-30"
      >
        <Activity size={12} className="text-secondary-main" />
        <span className="text-[10px] font-bold text-white">
          {t("landing.showcase.engagement", "+۲۴٪ تعامل")}
        </span>
      </motion.div>
    </motion.div>
  );
}

const FA_INBOX_ROWS = [
  {
    name: "علی محمدی",
    channel: "Instagram",
    msg: "این محصول موجوده؟ چقدره؟",
    time: "الان",
    avatar: "ع",
    unread: 2,
  },
  {
    name: "Sara K.",
    channel: "Telegram",
    msg: "ممنون بابت ارسال سریع 🌟",
    time: "۲ د",
    avatar: "S",
    unread: 0,
  },
  {
    name: "بوتیک نازی",
    channel: "WhatsApp",
    msg: "می‌تونم سفارش جدید ثبت کنم؟",
    time: "۵ د",
    avatar: "ب",
    unread: 1,
  },
  {
    name: "محسن R.",
    channel: "Bale",
    msg: "سلام، هزینهٔ ارسال چقدره؟",
    time: "۸ د",
    avatar: "م",
    unread: 0,
  },
];
